import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler } from '@angular/core';
import buildNumber from '@root/build_number';
import { environment } from '@root/environments/environment';
import rg4js, { RaygunPayload } from 'raygun4js';
import { apiURL, isFeatureEnabled, Utilities } from './utilities';

const MAX_ERRORS = 50;
let errorCount = 0;

type Payload = {
  error: any;
  tags: string[];
};

type Breadcrumb = {
  CustomData: {
    body: string;
    requestURL: string;
    response: number;
  };
};

const DEFAULTS = {
  localdev: {
    enabled: false
  },
  dev: {
    enabled: true
  },
  uat: {
    enabled: true
  },
  prod: {
    enabled: true
  }
};

const raygunEnv = isFeatureEnabled('localDev', false)
  ? 'localdev'
  : Utilities.currentEnvironment() || 'unknown';

const raygunTags: string[] = [raygunEnv];

const DEFAULT = DEFAULTS[raygunEnv]?.enabled ?? false;

window.useRaygun = DEFAULT;

initRaygun();

function groupingKeyCallback(payload: any, _stackTrace: any, _options: any) {
  return payload.Details?.Error?.Message;
}

function _redact(bc: Breadcrumb) {
  if (!bc.CustomData?.body || !bc.CustomData?.requestURL) {
    return;
  }
  // theoretically stringified JSON but longer strings are truncated with "(...)" and so cannot be parsed
  let body: string = bc.CustomData.body;

  if (bc.CustomData.requestURL.includes('/session/login')) {
    const bodyObj = JSON.parse(body);
    if (bodyObj.password) {
      bodyObj.password = '[REDACTED]';
      bc.CustomData.body = JSON.stringify(bodyObj);
    }
    return;
  }

  if (body.includes('password')) {
    bc.CustomData.body = '[REDACTED]';
    return;
  }

  // this are probably redundant given the above rule
  if (bc.CustomData.requestURL.includes('getMedusaCredentials')) {
    bc.CustomData.body = '[REDACTED]';
    return;
  }

  const cd = bc.CustomData as any;
  if (
    window.redactEntitiesInRaygun === true &&
    cd.responseURL?.includes('fx/get-user-info?task=getEntities')
  ) {
    bc.CustomData.body = '[REDACTED]';
    return;
  }
}

function redact(payload: RaygunPayload) {
  const breadcrumbs = payload.Details['Breadcrumbs'] as Breadcrumb[];
  breadcrumbs.forEach(bc => {
    try {
      _redact(bc);
    } catch {
      if (bc.CustomData?.body) {
        bc.CustomData.body = '[REDACTED]';
      }
    }
  });
}

function onBeforeSend(payload: RaygunPayload) {
  errorCount++;
  if (errorCount > MAX_ERRORS) {
    return false;
  }

  const message: string = payload.Details?.Error?.Message || '';
  // NG0100: ExpressionChangedAfterItHasBeenCheckedError.  Should only be in localDev
  if (message.includes('NG0100')) {
    return false;
  }

  try {
    redact(payload);
  } catch (e) {
    console.log('Payload redaction failed', e);
  }

  return payload;
}

function loadRaygunSettings() {
  const fromEnv = isFeatureEnabled('useRaygun', undefined);
  if (fromEnv !== undefined) {
    console.log('Raygun enabled?', fromEnv, '[Environment override]');
    window.useRaygun = fromEnv;
    return;
  }

  window.useRaygun = DEFAULT;
  const use = useRaygun();

  console.log('Raygun enabled?', use);
  if (!use) {
    console.log('Disabling raygun...');
    rg4js('detach');
    rg4js('disableAutoBreadcrumbsConsole');
  }
}

async function initRaygun() {
  console.log('Initializing Raygun', environment.raygunApiKey.substring(0, 4));
  rg4js('apiKey', environment.raygunApiKey);
  rg4js('setVersion', 'UNKNOWN');
  rg4js('enableCrashReporting', true);
  rg4js('logContentsOfXhrCalls', true);
  rg4js('options', {
    apiEndpoint: apiURL('/exotics/raygunproxy'),
    // This is not ideal. But maybe it's better than nothing to handle "Script error".   Better will be to
    // explicitly .catch() rejected promises, often by converting all the Promise<Observable<...>> to Observable<...>
    // see https://raygun.com/documentation/language-guides/javascript/crash-reporting/script-errors/
    captureUnhandledRejections: false
  });
  rg4js('withTags', raygunTags);
  rg4js('onBeforeSend', onBeforeSend);
  rg4js('groupingKey', groupingKeyCallback);
  rg4js('filterSensitiveData', ['password']);
  rg4js('setFilterScope', 'all');
  rg4js('setVersion', `${buildNumber}-${raygunEnv}`);
}

class RaygunErrorHandler implements ErrorHandler {
  constructor() {
    console.log('Raygun error handler created');
  }

  public handleError(error: any) {
    console.error('handleError(raygun)', error);
    if (!useRaygun()) {
      return;
    }
    let errStr = error.message || error;
    if (typeof errStr !== 'string') {
      try {
        errStr = JSON.stringify(errStr);
      } catch {}
    }
    console.log('Raygun error handler called', errStr);
    let rgErr: Error = error instanceof Error ? error : new Error(errStr);
    rg4js('send', { error: rgErr });
  }
}

function ignoreError(payload: Payload): boolean {
  try {
    if (!payload.error) {
      return false;
    }
    const exception = payload.error.exception;
    if (!exception) {
      return false;
    }

    const { type, message } = exception;

    if (type === 'NotLoggedIn') {
      // ignore {"exception":{"type":"NotLoggedIn","message":null}}
      // esp. from getEvents if user logged out
      return true;
    }
    // Old whitelabel (e.g., LOWL) have different error format
    // {"exception":{"type":"InvocationTargetException","message":"com.digitalvega.common.rpc.NotLoggedIn"}}
    if (message?.endsWith?.('NotLoggedIn')) {
      return true;
    }

    return false;
  } catch (e) {
    console.log(e);
    return false;
  }
}

function raygun(action: 'send', payload: Payload) {
  if (!useRaygun()) {
    return;
  }
  if (ignoreError(payload)) {
    return;
  }
  if (isFeatureEnabled('traceRaygun', false)) {
    console.trace(payload.error);
  } else if (payload.error) {
    console.error(payload.error);
  }

  if (payload.error && !(payload.error instanceof Error)) {
    try {
      payload.error = JSON.stringify(payload.error, null, 2);
    } catch {}
    payload.error = new Error(payload.error);
  }
  rg4js(action, payload);
}

function raygunHttpError(error: any, tags: string[]) {
  if (!useRaygun()) {
    return;
  }
  let errorResponse: any = null;
  if (error instanceof HttpErrorResponse) {
    errorResponse = error;
  } else if (error.response) {
    errorResponse = error.response;
  } else {
    errorResponse = {
      message: 'Unknown http error'
    };
  }
  const message =
    errorResponse?.message ||
    errorResponse?.statusText ||
    `Unknown error: ${errorResponse?.url} `;
  raygun('send', {
    error: new Error(message),
    tags
  });
}

function useRaygun() {
  return isFeatureEnabled('useRaygun', undefined) ?? window.useRaygun;
}

export {
  loadRaygunSettings,
  raygun,
  raygunEnv,
  RaygunErrorHandler,
  raygunHttpError,
  useRaygun
};
